import type { OnChanges, SimpleChanges } from '@angular/core';
import { Component, EventEmitter, Input, Output, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  AvatarComponent, BadgeComponent, ButtonComponent, CardComponent,
  ConfirmDialogComponent, EmptyStateComponent, SelectComponent, SpinnerComponent,
  ToastService, type SelectOption,
} from '@echospaces-web-monorepo/es-shared-ui';

import { AddMemberModalComponent } from './add-member-modal.component';
import { SpaceMembersService, type SpaceMember, type SpaceRole } from './space-members.service';

const ROLE_OPTIONS: SelectOption[] = [
  { value: 'visitor', label: 'Visitor' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

const ROLE_VARIANT: Record<SpaceRole, 'primary' | 'secondary' | 'success' | 'danger'> = {
  visitor: 'secondary',
  editor: 'primary',
  admin: 'danger',
};

/**
 * Members list for the currently selected space. Loads `getSpaceClaims`
 * for the space, and exposes Add / Change-Role / Remove actions when the
 * caller has Manage coverage (`canManage` input drives the gating).
 */
@Component({
  selector: 'es-space-members-tab',
  imports: [
    FormsModule, CardComponent, AvatarComponent, BadgeComponent, ButtonComponent,
    SpinnerComponent, EmptyStateComponent, SelectComponent,
    ConfirmDialogComponent, AddMemberModalComponent,
  ],
  template: `
    @if (members.loading()) {
      <es-spinner label="Loading members…"></es-spinner>
    } @else if (members.members().length === 0) {
      <es-empty-state
        title="No members"
        [description]="canManage
          ? 'Add the first member to delegate access to this space.'
          : 'No one has explicit access to this space. Ancestor grants may still apply.'"
        icon="👥"
        [actionLabel]="canManage ? 'Add member' : undefined"
        (action)="openAddModal()"
      ></es-empty-state>
    } @else {
      <div class="space-y-3">
        @if (canManage) {
          <div class="flex justify-end">
            <es-button variant="primary" size="sm" (clicked)="openAddModal()">
              + Add member
            </es-button>
          </div>
        }

        @for (member of members.members(); track member.userId) {
          <es-card>
            <div class="flex items-center gap-3">
              <es-avatar [name]="member.email ?? member.userId" size="sm"></es-avatar>
              <div class="flex-1 min-w-0">
                <p class="font-medium truncate">{{ member.email ?? '(unknown email)' }}</p>
                <p class="text-xs text-[var(--text-secondary)] font-mono truncate">{{ member.userId }}</p>
              </div>

              @if (canManage) {
                <es-select
                  [options]="roleOptions"
                  [ngModel]="member.role"
                  (ngModelChange)="onRoleChange(member, $event)"
                ></es-select>
                <es-button
                  variant="ghost"
                  size="sm"
                  (clicked)="askRemove(member)"
                >Remove</es-button>
              } @else {
                <es-badge [label]="member.role" [variant]="roleVariant(member.role)"></es-badge>
              }
            </div>
          </es-card>
        }
      </div>
    }

    <!-- Add-member modal -->
    <es-add-member-modal
      #addModal
      [open]="addModalOpen()"
      (closed)="addModalOpen.set(false)"
      (submitted)="onAddSubmit($event)"
    ></es-add-member-modal>

    <!-- Confirm-remove dialog -->
    @if (memberToRemove(); as target) {
      <es-confirm-dialog
        [open]="true"
        title="Remove member?"
        [message]="'Revoke ' + (target.email ?? target.userId) + ' from this space?'"
        confirmLabel="Remove"
        [destructive]="true"
        (confirmed)="confirmRemove(target)"
        (cancelled)="memberToRemove.set(null)"
      ></es-confirm-dialog>
    }
  `,
})
export class SpaceMembersTabComponent implements OnChanges {
  @Input({ required: true }) spaceId!: string;
  @Input() canManage = false;

  @Output() membersChanged = new EventEmitter<void>();

  @ViewChild('addModal') addModal?: AddMemberModalComponent;

  readonly members = inject(SpaceMembersService);
  private readonly toast = inject(ToastService);

  readonly roleOptions = ROLE_OPTIONS;
  readonly addModalOpen = signal(false);
  readonly memberToRemove = signal<SpaceMember | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['spaceId'] && this.spaceId) {
      this.members.loadMembers(this.spaceId);
    }
  }

  roleVariant(role: SpaceRole) {
    return ROLE_VARIANT[role];
  }

  openAddModal() {
    this.addModalOpen.set(true);
  }

  async onAddSubmit(payload: { userId: string; role: SpaceRole }) {
    try {
      await this.members.addMember(this.spaceId, payload.userId, payload.role);
      this.addModal?.finishSubmit();
      this.toast.success('Member added');
      this.addModalOpen.set(false);
      this.membersChanged.emit();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add member';
      this.addModal?.finishSubmit(message);
    }
  }

  async onRoleChange(member: SpaceMember, newRole: SpaceRole) {
    if (newRole === member.role) return;
    try {
      await this.members.updateMemberRole(this.spaceId, member.userId, newRole);
      this.toast.success(`Role updated to ${newRole}`);
      this.membersChanged.emit();
    } catch {
      this.toast.error('Failed to update role');
    }
  }

  askRemove(member: SpaceMember) {
    this.memberToRemove.set(member);
  }

  async confirmRemove(member: SpaceMember) {
    this.memberToRemove.set(null);
    try {
      await this.members.removeMember(this.spaceId, member.userId);
      this.toast.success('Member removed');
      this.membersChanged.emit();
    } catch {
      this.toast.error('Failed to remove member');
    }
  }
}
